import { useCallback, useEffect, useState } from "react"
import cityData from "./cityData.json" assert { type: "json" }
import { useFilters } from "./useFilters"
import { usePathname, useRouter, useSearchParams } from "next/navigation"
import { JobPostingLocationType } from "@/gql/graphql"

const popularLocations = [
  "Los Angeles, CA",
  "New York, NY",
  "London, UK",
  "Berlin, DE",
  "Paris, FR",
  "Miami, FL",
  "Boston, MA",
  "San Francisco, CA",
  "Austin, TX",
  "Denver, CO",
  "Chicago, IL",
  "Seattle, WA",
]

const popularLocationsWithRemote = ["Remote", ...popularLocations]

type CityJSONData = {
  [key: string]: {
    lat: string
    lon: string
  }
}

const cityDataTyped: CityJSONData = cityData
const cities = Object.keys(cityDataTyped)

export type LocationItem = {
  name: string
  lat: string | null
  lon: string | null
}

type Locations = LocationItem[]

export const formatResponse = (result: any) => {
  let city =
    result.components.city ||
    result.components.town ||
    result.components.village ||
    result.components.borough ||
    result.components.county ||
    result.components.hamlet ||
    result.components.water

  if (!city) {
    city = result.formatted.split(",")[0]
  }

  const manualyFormatedName = `${city}, ${
    result.components?.state_code ? result.components.state_code : ""
  } ${result.components.country_code === "us" ? "" : result.components.country}`

  const name = manualyFormatedName.includes("undefined") ? result.formatted : manualyFormatedName

  return {
    name,
    lat: result.geometry.lat,
    lon: result.geometry.lng,
  }
}

export const useLocationFilter = ({
  search,
  id,
  showRemote = true,
}: {
  search: string | null
  id?: string
  showRemote?: boolean
}) => {
  const [locations, setLocations] = useState<Locations>([])
  const searchParams = useSearchParams()
  const router = useRouter()
  const pathname = usePathname()

  const { onToggleMenu } = useFilters()

  const toggleFilter = ({ location }: { location: LocationItem }) => {
    const current = searchParams ? new URLSearchParams(Array.from(searchParams.entries())) : new URLSearchParams()
    if (location.name === "Remote") {
      current.delete("location")
      current.set("locationType", JobPostingLocationType.Telecommute)
    } else {
      const radius = 30
      const locationVal = location.lat && location.lon ? `${location.lat},${location.lon},${radius}` : ""
      current.delete("locationType")
      current.set("location", locationVal)
    }
    current.set("locationName", location.name)
    if (id) {
      onToggleMenu(id)
    }
    const query = current.toString() ? `?${current.toString()}` : ""
    router.push(`${pathname}${query}`)
  }

  const searchLocalCities = useCallback((searchTerm: string): Locations => {
    const lowerSearchTerm = searchTerm.toLowerCase()
    return cities
      .filter(city => city.toLowerCase().includes(lowerSearchTerm))
      .map(city => ({
        name: city,
        lat: cityDataTyped[city].lat,
        lon: cityDataTyped[city].lon,
      }))
  }, [])

  const getData: () => Promise<Locations> = useCallback(async () => {
    if (!search) {
      const locations = showRemote ? popularLocationsWithRemote : popularLocations
      return Promise.resolve(
        locations.map(location => ({
          name: location,
          //  @ts-ignore
          lat: cityData[location]?.lat || null,
          //  @ts-ignore
          lon: cityData[location]?.lon || null,
        }))
      )
    }

    // First, try to find local matches
    const localMatches = searchLocalCities(search)
    if (localMatches.length > 0) {
      return Promise.resolve(localMatches)
    }

    // If no local matches, fall back to the OpenCage API
    const response = await fetch(`/api/geocode?search=${search}`)
    const respJson = await response.json()

    if (respJson && respJson?.data && respJson.data.length > 0) {
      return respJson.data.map((result: any) => {
        return formatResponse(result)
      })
    }

    return []
  }, [search, searchLocalCities])

  useEffect(() => {
    getData().then(data => {
      setLocations(data)
    })
  }, [setLocations, search, getData])

  return { locations, toggleFilter }
}
